import JSZip from 'jszip';
import { readPsd, writePsd } from 'ag-psd';

export function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width)); canvas.height = Math.max(1, Math.round(height));
  return canvas;
}
export function cloneCanvas(source) {
  const canvas = createCanvas(source.width, source.height);
  canvas.getContext('2d', { willReadFrequently: true }).drawImage(source, 0, 0);
  return canvas;
}
export function canvasToBlob(canvas, type = 'image/png', quality = 0.92) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error(`Could not encode ${type}`)), type, quality));
}
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = fileName; document.body.append(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 3000);
}
export async function imageFileToCanvas(file) {
  const bitmap = await createImageBitmap(file); const canvas = createCanvas(bitmap.width, bitmap.height); canvas.getContext('2d').drawImage(bitmap, 0, 0); bitmap.close?.(); return canvas;
}
export async function createProjectBlob(doc) {
  const zip = new JSZip();
  const manifest = {
    format: 'application/vnd.zenbinary.picplayer-project', version: 1,
    document: { name: doc.name, width: doc.width, height: doc.height, resolution: doc.resolution || 72, selectedLayerId: doc.selectedLayerId, background: doc.background || 'transparent' },
    layers: [], createdWith: 'PicPlayer 0.1.0', createdAt: new Date().toISOString(),
  };
  for (const layer of doc.layers) {
    const file = `layers/${layer.id}.png`; const blob = await canvasToBlob(layer.canvas, 'image/png'); zip.file(file, blob);
    manifest.layers.push({ id: layer.id, name: layer.name, type: layer.type || 'raster', x: layer.x || 0, y: layer.y || 0, opacity: layer.opacity ?? 1, blendMode: layer.blendMode || 'normal', visible: layer.visible !== false, locked: !!layer.locked, file });
  }
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.zenbinary.picplayer-project', compression: 'DEFLATE', compressionOptions: { level: 5 } });
}
export async function readProjectFile(file) {
  const zip = await JSZip.loadAsync(await file.arrayBuffer()); const manifestFile = zip.file('manifest.json');
  if (!manifestFile) throw new Error('This is not a valid PicPlayer project.');
  const manifest = JSON.parse(await manifestFile.async('text'));
  if (manifest.format !== 'application/vnd.zenbinary.picplayer-project' || manifest.version !== 1) throw new Error('Unsupported PicPlayer project version.');
  const layers = [];
  for (const item of manifest.layers || []) {
    const layerFile = zip.file(item.file); if (!layerFile) continue;
    const bitmap = await createImageBitmap(await layerFile.async('blob')); const canvas = createCanvas(bitmap.width, bitmap.height); canvas.getContext('2d').drawImage(bitmap, 0, 0); bitmap.close?.();
    layers.push({ ...item, canvas });
  }
  return { ...manifest.document, layers, format: 'picproj' };
}
function flattenPsdChildren(children, target, prefix = '') {
  for (const child of children || []) {
    const pathName = prefix ? `${prefix} / ${child.name || 'Layer'}` : (child.name || 'Layer');
    if (child.children) flattenPsdChildren(child.children, target, pathName);
    if (child.canvas) {
      target.push({
        id: `layer_${crypto.randomUUID()}`, name: pathName, type: child.text ? 'text-rasterized' : child.vectorMask ? 'vector-rasterized' : 'raster',
        canvas: cloneCanvas(child.canvas), x: Number(child.left || 0), y: Number(child.top || 0), opacity: child.opacity ?? 1,
        blendMode: String(child.blendMode || 'normal').replaceAll(' ', '-'), visible: !child.hidden, locked: !!child.protected?.position,
      });
    }
  }
}
export async function readPsdFile(file) {
  let psd;
  try { psd = readPsd(await file.arrayBuffer(), { skipThumbnail: true }); }
  catch (error) { throw new Error(`PSD could not be read: ${error.message}`); }
  const layers = []; flattenPsdChildren(psd.children, layers);
  if (!layers.length && psd.canvas) layers.push({ id: `layer_${crypto.randomUUID()}`, name: 'Flattened PSD', type: 'raster', canvas: cloneCanvas(psd.canvas), x: 0, y: 0, opacity: 1, blendMode: 'normal', visible: true, locked: false });
  if (!layers.length) throw new Error('The PSD contains no renderable layer pixels.');
  return { name: file.name.replace(/\.psd$/i, ''), width: psd.width, height: psd.height, resolution: psd.imageResources?.resolutionInfo?.horizontalResolution || 72, layers, selectedLayerId: layers[0].id, format: 'psd', compatibility: { bitsPerChannel: psd.bitsPerChannel || 8, colorMode: psd.colorMode, flattenedGroups: true } };
}
export function compositeDocument(doc) {
  const canvas = createCanvas(doc.width, doc.height); const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, doc.width, doc.height);
  for (const layer of [...doc.layers].reverse()) {
    if (!layer.visible || layer.opacity <= 0) continue;
    ctx.save(); ctx.globalAlpha = layer.opacity ?? 1; ctx.globalCompositeOperation = cssBlendToCanvas(layer.blendMode); ctx.drawImage(layer.canvas, layer.x || 0, layer.y || 0); ctx.restore();
  }
  return canvas;
}
export function cssBlendToCanvas(mode) {
  const supported = new Set(['source-over','multiply','screen','overlay','darken','lighten','color-dodge','color-burn','hard-light','soft-light','difference','exclusion','hue','saturation','color','luminosity']);
  return supported.has(mode) ? mode : 'source-over';
}
export async function createPsdBlob(doc) {
  const composite = compositeDocument(doc);
  const children = doc.layers.map((layer) => ({ name: layer.name, canvas: layer.canvas, left: Math.round(layer.x || 0), top: Math.round(layer.y || 0), opacity: layer.opacity ?? 1, blendMode: String(layer.blendMode || 'normal').replaceAll('-', ' '), hidden: !layer.visible }));
  const buffer = writePsd({ width: doc.width, height: doc.height, children, canvas: composite });
  return new Blob([buffer], { type: 'image/vnd.adobe.photoshop' });
}
export function extensionOf(name) { const match = String(name || '').toLowerCase().match(/\.([^.]+)$/); return match ? match[1] : ''; }
export function ensureExtension(name, extension) { return name.toLowerCase().endsWith(`.${extension}`) ? name : `${name}.${extension}`; }
